import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Spinner, Alert, Tab, Nav, Modal } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import Layout from '../../components/layouts/Main';
import VehicleList from '../../components/master/VehicleList';
import DriverList from '../../components/master/DriverList';
import VehicleForm from '../../components/master/VehicleForm';
import DriverForm from '../../components/master/DriverForm';
import TransporterForm from '../../components/master/TransporterForm';

const TransporterDetails = () => {
    const { transporterId } = useParams();
    const [transporter, setTransporter] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showVehicleModal, setShowVehicleModal] = useState(false);
    const [showDriverModal, setShowDriverModal] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const fetchTransporter = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${config.API_BASE_URL}/transporters/${transporterId}`);
            setTransporter(res.data);
        } catch (error) {
            toast.error('Failed to fetch transporter details.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [transporterId]);

    const fetchVehicles = useCallback(async () => {
        try {
            const res = await axios.get(`${config.API_BASE_URL}/vehicles/by-transporter/${transporterId}`);
            setVehicles(res.data || []);
        } catch (err) {
            toast.error('Failed to fetch vehicles');
        }
    }, [transporterId]);

    const fetchDrivers = useCallback(async () => {
        try {
            const res = await axios.get(`${config.API_BASE_URL}/drivers/by-transporter/${transporterId}`);
            setDrivers(res.data || []);
        } catch (err) {
            toast.error('Failed to fetch drivers');
        }
    }, [transporterId]);

    useEffect(() => {
        fetchTransporter();
        fetchVehicles();
        fetchDrivers();
    }, [fetchTransporter, fetchVehicles, fetchDrivers]);

    const handleTransporterSave = async (transporterData) => {
        setLoading(true);
        try {
            await axios.put(`${config.API_BASE_URL}/transporters/${transporterId}`, transporterData);
            toast.success('Transporter updated successfully');
            fetchTransporter();
            setShowEditModal(false);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save transporter');
        } finally {
            setLoading(false);
        }
    };


    const handleVehicleSave = async (vehicleData) => {
        setLoading(true);
        try {
            if (vehicleData._id) {
                await axios.put(`${config.API_BASE_URL}/vehicles/${vehicleData._id}`, vehicleData);
                toast.success('Vehicle updated successfully');
            } else {
                await axios.post(`${config.API_BASE_URL}/vehicles`, { ...vehicleData, transporterId });
                toast.success('Vehicle created successfully');
            }
            fetchVehicles();
            setShowVehicleModal(false);
            setSelectedVehicle(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save vehicle');
        } finally {
            setLoading(false);
        }
    };

    const handleDriverSave = async (formData) => {
        setLoading(true);
        try {
            const driverId = formData.get('_id');
            if (driverId) {
                await axios.put(`${config.API_BASE_URL}/drivers/${driverId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Driver updated successfully');
            } else {
                if (!formData.get('transporterId')) {
                    formData.append('transporterId', transporterId);
                }
                await axios.post(`${config.API_BASE_URL}/drivers`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Driver created successfully');
            }
            fetchDrivers();
            setShowDriverModal(false);
            setSelectedDriver(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save driver');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!showDeleteConfirm) return;
        setLoading(true);
        try {
            if (showDeleteConfirm.type === 'vehicle') {
                await axios.delete(`${config.API_BASE_URL}/vehicles/${showDeleteConfirm.data._id}`);
                toast.success('Vehicle deactivated successfully');
                fetchVehicles();
            } else if (showDeleteConfirm.type === 'driver') {
                await axios.delete(`${config.API_BASE_URL}/drivers/${showDeleteConfirm.data._id}`);
                toast.success('Driver deactivated successfully');
                fetchDrivers();
            }
            setShowDeleteConfirm(null);
        } catch (err) {
            toast.error(`Failed to deactivate ${showDeleteConfirm.type}`);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !transporter) {
        return <Layout><div className="text-center" ><Spinner animation="border" /></div></Layout>;
    }

    if (!transporter) {
        return <Layout><Alert variant="danger">Transporter not found.</Alert></Layout>;
    }

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                    <li className="breadcrumb-item"><Link to="/transporters">Transporters</Link></li>
                    <li className="breadcrumb-item active">{transporter.name}</li>
                </ol>
            </nav>
            <Row className="mb-3">
                <Col>
                    <h2 className="mb-0">{transporter.name}</h2>
                    <p className="text-muted">Transporter Details</p>
                </Col>
                <Col className="text-end">
                    <Button variant="primary" onClick={() => setShowEditModal(true)}>Edit Transporter</Button>
                </Col>
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Information</Card.Title>
                    <Row>
                        <Col md={6}>
                            <p><strong>Contact Number:</strong> {transporter.contactNumber || 'N/A'}</p>
                            <p><strong>GSTIN:</strong> {transporter.gstin || 'N/A'}</p>
                        </Col>
                        <Col md={6}>
                            <p><strong>Address:</strong> {transporter.address || 'N/A'}</p>
                            <p><strong>Status:</strong>
                                <span className={`badge bg-${transporter.isActive ? 'success' : 'danger'}`}>
                                    {transporter.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Tab.Container defaultActiveKey="vehicles">
                <Card>
                    <Card.Header>
                        <Nav variant="tabs">
                            <Nav.Item>
                                <Nav.Link eventKey="vehicles">Vehicles</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="drivers">Drivers</Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Card.Header>
                    <Card.Body>
                        <Tab.Content>
                            <Tab.Pane eventKey="vehicles">
                                <Button variant="primary" className="mb-3" onClick={() => { setSelectedVehicle(null); setShowVehicleModal(true); }}>
                                    Add Vehicle
                                </Button>
                                <VehicleList
                                    vehicles={vehicles}
                                    loading={loading}
                                    onEdit={(v) => { setSelectedVehicle(v); setShowVehicleModal(true); }}
                                    onDelete={(v) => setShowDeleteConfirm({ type: 'vehicle', data: v })}
                                />
                            </Tab.Pane>
                            <Tab.Pane eventKey="drivers">
                                <Button variant="primary" className="mb-3" onClick={() => { setSelectedDriver(null); setShowDriverModal(true); }}>
                                    Add Driver
                                </Button>
                                <DriverList
                                    drivers={drivers}
                                    vehicles={vehicles}
                                    loading={loading}
                                    onEdit={(d) => { setSelectedDriver(d); setShowDriverModal(true); }}
                                    onDelete={(d) => setShowDeleteConfirm({ type: 'driver', data: d })}
                                />
                            </Tab.Pane>
                        </Tab.Content>
                    </Card.Body>
                </Card>
            </Tab.Container>

            <Modal show={showVehicleModal} onHide={() => { setShowVehicleModal(false); setSelectedVehicle(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedVehicle ? 'Edit' : 'Add'} Vehicle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <VehicleForm
                        vehicle={selectedVehicle}
                        onSave={handleVehicleSave}
                        onCancel={() => { setShowVehicleModal(false); setSelectedVehicle(null); }}
                        loading={loading}
                    />
                </Modal.Body>
            </Modal>

            <Modal show={showDriverModal} onHide={() => { setShowDriverModal(false); setSelectedDriver(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedDriver ? 'Edit' : 'Add'} Driver</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <DriverForm
                        driver={selectedDriver}
                        onSave={handleDriverSave}
                        onCancel={() => { setShowDriverModal(false); setSelectedDriver(null); }}
                        loading={loading}
                        transporterId={transporterId}
                        vehicles={vehicles}
                    />
                </Modal.Body>
            </Modal>
            
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Transporter</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <TransporterForm
                        transporter={transporter}
                        onSave={handleTransporterSave}
                        onCancel={() => setShowEditModal(false)}
                        loading={loading}
                    />
                </Modal.Body>
            </Modal>

            <Modal show={!!showDeleteConfirm} onHide={() => setShowDeleteConfirm(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deactivation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to deactivate this {showDeleteConfirm?.type}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(null)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={loading}>
                        {loading ? 'Deactivating...' : 'Deactivate'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Layout>
    );
};

export default TransporterDetails;
